import { Star } from "@mui/icons-material";
import { Alert, Box, Button, Card, Grid, Rating, Snackbar, TextareaAutosize, Typography } from "@mui/material";
import { Stack } from "@mui/system";
import React, { useState } from "react";
import { useStore } from "../../../../store/useStore";
import { tokens } from "../../../../store/tokens";
import LoginSignUpFormMini from "../../../OrderCheckOut/LoginSignUpFormMini";

const RATING_BUCKETS = [5, 4, 3, 2, 1];

const RatingBar = ({ stars, count, total }) => {
  const pct = total > 0 ? Math.round(((count ?? 0) / total) * 100) : 0;

  return (
    <Stack direction={"row"} spacing={1.5} alignItems={"center"}>
      <Stack direction={"row"} spacing={0.25} alignItems={"center"} sx={{ width: 32 }}>
        <Typography variant="body2" sx={{ fontWeight: 700, color: tokens.color.ink }}>
          {stars}
        </Typography>
        <Star sx={{ fontSize: "1rem", color: tokens.color.warning }} />
      </Stack>
      <Box
        sx={{
          flex: 1,
          height: 8,
          borderRadius: `${tokens.radius.pill}px`,
          bgcolor: tokens.color.surfaceMuted,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: `${pct}%`,
            height: "100%",
            bgcolor: tokens.color.warning,
            borderRadius: `${tokens.radius.pill}px`,
            transition: "width 0.3s ease",
          }}
        />
      </Box>
      <Typography variant="body2" sx={{ width: 32, textAlign: "right", color: tokens.color.inkMuted }}>
        {count ?? 0}
      </Typography>
    </Stack>
  );
};

const Review = (props) => {
  const { fetchedReview } = props;
  const {
    total_review,
    three_star_review,
    one_star_review,
    four_star_review,
    five_star_review,
    two_star_review,
    average_rating,
  } = fetchedReview;

  const counts = {
    5: five_star_review,
    4: four_star_review,
    3: three_star_review,
    2: two_star_review,
    1: one_star_review,
  };

  // The real "am I logged in" gate — derived live from the store, so the
  // moment LoginSignUpFormMini's login/register succeeds and calls
  // setUser(), this flips and the review form takes over automatically.
  const { user } = useStore();
  const isLoggedIn = Boolean(user?.sopnoid);

  const [openReview, setOpenReview] = useState(false);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewText, setReviewText] = useState("");

  // Feedback surface for LoginSignUpFormMini, which reports progress and
  // errors via handleSnackOpen — without this those messages had nowhere
  // to render.
  const [snackMessage, setSnackMessage] = useState("");
  const [snackOpen, setSnackOpen] = useState(false);
  const handleSnackOpen = (message) => {
    setSnackMessage(message);
    setSnackOpen(true);
  };
  const handleSnackClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackOpen(false);
  };

  // Local state for the embedded auth form. `setIsLogged` below is only
  // there to satisfy the form's contract (it calls it on success) — the
  // actual gate is `isLoggedIn` from the store, so this never needs to be
  // read directly.
  const [isLogin, setIsLogin] = useState(true);
  const [authLoading, setAuthLoading] = useState(false);
  const [, setIsLoggedLocal] = useState(false);

  const handleSubmitReview = () => {
    // TODO: wire to the actual submit-review endpoint once available.
    // Expect something like POST review_fetch_expert.php with
    // { rating: reviewRating, review: reviewText }.
    setOpenReview(false);
    setReviewRating(0);
    setReviewText("");
  };

  return (
    <>
      <Snackbar
        open={snackOpen}
        autoHideDuration={4000}
        onClose={handleSnackClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleSnackClose} severity="info" sx={{ width: "100%" }}>
          {snackMessage}
        </Alert>
      </Snackbar>

      <Card
        elevation={0}
        sx={{
          padding: `${tokens.card.padding.md}px`,
          borderRadius: `${tokens.card.radius}px`,
          border: `1px solid ${tokens.color.line}`,
        }}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} sm={5}>
            <Stack spacing={1.5} alignItems={"center"} justifyContent={"center"} sx={{ height: "100%" }}>
              <Stack direction={"row"} spacing={1} alignItems={"center"}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: tokens.color.ink }}>
                  {average_rating ?? 0}
                </Typography>
                <Typography variant="body1" sx={{ color: tokens.color.inkMuted }}>
                  / 5
                </Typography>
              </Stack>
              <Rating
                name="read-only"
                value={parseFloat(average_rating) || 0}
                readOnly
                sx={{ "& .MuiRating-iconFilled": { color: tokens.color.warning } }}
              />
              <Typography variant="body2" sx={{ color: tokens.color.inkMuted }}>
                {total_review ?? 0} reviews
              </Typography>

              <Button
                variant="contained"
                size="small"
                onClick={() => setOpenReview((v) => !v)}
                sx={{
                  bgcolor: tokens.color.teal,
                  textTransform: "none",
                  borderRadius: `${tokens.radius.pill}px`,
                  "&:hover": { bgcolor: tokens.color.tealDark },
                }}
              >
                Write a review
              </Button>

              {openReview && isLoggedIn && (
                <Card
                  elevation={0}
                  sx={{
                    width: "100%",
                    padding: 2,
                    border: `1px solid ${tokens.color.line}`,
                    borderRadius: `${tokens.card.radius}px`,
                  }}
                >
                  <Stack spacing={1.5}>
                    <Stack direction={"row"} justifyContent={"center"}>
                      <Rating
                        name="new-review-rating"
                        value={reviewRating}
                        onChange={(event, newValue) => setReviewRating(newValue)}
                      />
                    </Stack>
                    <TextareaAutosize
                      minRows={3}
                      placeholder="Share your experience…"
                      value={reviewText}
                      onChange={(e) => setReviewText(e.target.value)}
                      style={{
                        width: "100%",
                        fontFamily: tokens.font.body,
                        fontSize: "0.875rem",
                        padding: "10px 12px",
                        borderRadius: `${tokens.input.radius}px`,
                        border: `1px solid ${tokens.input.border}`,
                        resize: "vertical",
                      }}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      disabled={reviewRating === 0}
                      onClick={handleSubmitReview}
                      sx={{
                        alignSelf: "flex-end",
                        bgcolor: tokens.color.tealDark,
                        textTransform: "none",
                        borderRadius: `${tokens.radius.pill}px`,
                        "&:hover": { bgcolor: tokens.color.teal },
                      }}
                    >
                      Submit
                    </Button>
                  </Stack>
                </Card>
              )}

              {openReview && !isLoggedIn && (
                <Box sx={{ width: "100%" }}>
                  <Typography
                    variant="body2"
                    sx={{ color: tokens.color.inkMuted, textAlign: "center", mb: 1 }}
                  >
                    Log in to write a review
                  </Typography>
                  <Box
                    sx={{
                      border: `1px solid ${tokens.color.line}`,
                      borderRadius: `${tokens.card.radius}px`,
                      overflow: "hidden",
                    }}
                  >
                    <LoginSignUpFormMini
                      isLogin={isLogin}
                      setIsLogin={setIsLogin}
                      loading={authLoading}
                      setLoading={setAuthLoading}
                      setIsSubmitting={() => {}}
                      setIsLogged={setIsLoggedLocal}
                      handleSnackOpen={handleSnackOpen}
                    />
                  </Box>
                </Box>
              )}
            </Stack>
          </Grid>

          <Grid item xs={12} sm={7}>
            <Stack spacing={1.25} justifyContent={"center"} sx={{ height: "100%" }}>
              {RATING_BUCKETS.map((stars) => (
                <RatingBar key={stars} stars={stars} count={counts[stars]} total={total_review} />
              ))}
            </Stack>
          </Grid>
        </Grid>
      </Card>
    </>
  );
};

export default Review;